import groupBy from 'lodash/groupBy';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useChatSettings } from '@guuru/chat-web';
import ChatMessageBubble from '../../ChatMessageBubble';
import OptionsList from '../../OptionsList';
import TextMessage from '../../TextMessage';

type OptionsListProps = React.ComponentProps<typeof OptionsList>;
type SelectedQuickAction = Parameters<OptionsListProps['onSelection']>[0];

interface ChatRenderQuickActionsProps {
  message: {
    createdAt: number;
    i18n: string;
    metadata: {
      quickActions: OptionsListProps['options'];
    };
  };
  onRunNextStep?: (_: {
    quickAction: SelectedQuickAction;
    createChatImmediately?: boolean | null;
  }) => void;
}

const ChatRenderQuickActions = function ({
  message,
  onRunNextStep = () => {},
}: ChatRenderQuickActionsProps) {
  const { t } = useTranslation();
  const { data: { chatSettings: { quickActionsSettings } = {} } = {} } =
    useChatSettings();

  const skipIfAdviceQuestion =
    quickActionsSettings?.quickActionSkipIfAdviceQuestion;

  const handleQuickActionSelection = (quickAction: SelectedQuickAction) => {
    onRunNextStep({
      quickAction,
      createChatImmediately: skipIfAdviceQuestion,
    });
  };

  const { quickActions } = message.metadata;

  if (!quickActions?.length) {
    return (
      <ChatMessageBubble>
        <TextMessage>{message.i18n}</TextMessage>
      </ChatMessageBubble>
    );
  }

  const liveChatOnTop = quickActionsSettings?.quickActionRequestQuestionsTop;

  const groups = groupBy(quickActions, (channel) =>
    channel.action === 'requestQuestion' ? 'liveChat' : 'general',
  );
  const guurusBlockMsg = t('bot:::requestQuickActionSelectionGuurus');
  const liveChat = { message: guurusBlockMsg, quickActions: groups.liveChat };
  const general = { message: message.i18n, quickActions: groups.general };
  const quickActionsByType = liveChatOnTop
    ? [liveChat, general]
    : [general, liveChat];

  return (
    <ChatMessageBubble fullWidth>
      {quickActionsByType
        .filter(
          ({ quickActions: groupQuickActions }) => groupQuickActions?.length,
        )
        .map(({ message: groupMessage, quickActions: groupQuickActions }) => (
          <OptionsList
            key={groupMessage}
            message={groupMessage}
            options={groupQuickActions}
            onSelection={handleQuickActionSelection}
            orderKey="priority"
          />
        ))}
    </ChatMessageBubble>
  );
};

export default ChatRenderQuickActions;
